import { useEffect, useCallback } from 'react';
import { clsx } from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearch } from '@/context/SearchContext';
import { useGeolocation } from '@/hooks/useGeolocation';
import { useMapboxGeocoding } from '@/hooks/useMapboxGeocoding';

interface LocationButtonProps {
  className?: string;
  variant?: 'primary' | 'secondary';
}

export function LocationButton({ className, variant = 'primary' }: LocationButtonProps) {
  const { setLocation } = useSearch();
  const { location, isLocating, error, requestLocation, clearError } = useGeolocation();
  const { reverseGeocode, isSearching: isReversing } = useMapboxGeocoding();

  // When we get a location, reverse geocode it
  useEffect(() => {
    if (!location) return;

    void (async () => {
      const result = await reverseGeocode(location.latitude, location.longitude);
      setLocation(
        { latitude: location.latitude, longitude: location.longitude },
        result?.name ?? 'Current Location'
      );
    })();
  }, [location, reverseGeocode, setLocation]);

  const handleClick = useCallback(() => {
    clearError();
    void requestLocation();
  }, [clearError, requestLocation]);

  const isLoading = isLocating || isReversing;

  const buttonStyles = {
    primary: clsx(
      'flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl font-medium',
      'bg-brand-600 text-white hover:bg-brand-700',
      'focus:outline-none focus:ring-4 focus:ring-brand-200',
      'transition-all duration-200',
      'disabled:opacity-50 disabled:cursor-not-allowed'
    ),
    secondary: clsx(
      'flex items-center justify-center gap-2 p-2.5 rounded-lg',
      'bg-gray-100 text-gray-700 hover:bg-gray-200',
      'focus:outline-none focus:ring-2 focus:ring-gray-300',
      'transition-all duration-200',
      'disabled:opacity-50 disabled:cursor-not-allowed'
    ),
  };

  return (
    <div className={clsx('relative', className)}>
      <button
        type="button"
        onClick={handleClick}
        disabled={isLoading}
        className={buttonStyles[variant]}
      >
        {isLoading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-2"
          >
            <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            <span className={variant === 'secondary' ? 'sr-only' : ''}>
              {isLocating ? 'Finding...' : 'Getting name...'}
            </span>
          </motion.div>
        ) : (
          <>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span className={variant === 'secondary' ? 'sr-only' : ''}>Use my location</span>
          </>
        )}
      </button>

      {/* Error toast */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 left-1/2 -translate-x-1/2 top-full mt-2 w-64"
          >
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm shadow-lg">
              <div className="flex items-start gap-2">
                <svg
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{error.message}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
